import { Trans, useLingui } from "@lingui/react/macro";
import {
  careEpisodeAssetsQueryOptions,
  deallocateAsset,
} from "@/api/Equipment";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import * as Sentry from "@sentry/react";
import { Text } from "@medoma/components/Text/Text";
import { Heading } from "@medoma/components/Heading/Heading";
import { OutlinedButton } from "@medoma/components/Button/Button";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import { humanFormat } from "@medoma/models/date-and-time";
import { CommentEditor } from "./CommentEditor";
import styles from "./AllocatedAssets.module.scss";

type Asset = Awaited<
  ReturnType<ReturnType<typeof careEpisodeAssetsQueryOptions>["queryFn"]>
>[number];

type AllocatedAssetsProps = {
  patientId: string;
};

export const AllocatedAssets = ({ patientId }: AllocatedAssetsProps) => {
  const {
    data: assets,
    isPending,
    isError,
    error,
  } = useQuery(careEpisodeAssetsQueryOptions({ careEpisodeId: patientId }));
  const { t } = useLingui();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const queryClient = useQueryClient();
  const { confirm } = useDialog();

  const {
    mutate: returnAsset,
    isPending: isReturningAsset,
    variables: returningAssetVariables,
  } = useMutation({
    mutationFn: deallocateAsset,
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att återlämna utrustningen.`} ${deducedError(error)}`,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["equipment"] });
    },
  });

  const handleReturn = async (asset: Asset) => {
    const assetName = asset.name;
    const confirmed = await confirm({
      title: t`Bekräfta återlämning`,
      message: t`Är du säker på att ${assetName} har återlämnats?`,
    });
    if (!confirmed) {
      return;
    }
    returnAsset({ assetId: asset.id });
  };

  if (isPending) {
    return (
      <Loading
        message={t`Hämtar information om tilldelad utrustning`}
        padding={24}
      />
    );
  }

  if (isError) {
    Sentry.captureException(error);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta information om tilldelad utrustning.`} ${deducedError(error)}`}
        padding={24}
      />
    );
  }

  return (
    <ul className={styles.assets}>
      {assets.map((asset) => (
        <li key={asset.id} className={styles.asset}>
          <div className={styles.header}>
            <div className={styles.nameAndCategory}>
              <Heading level="h3" size="large" weight="medium">
                {asset.name}
              </Heading>
              <Text element="span" color="faded">
                {asset.categoryName}
              </Text>
            </div>
            <OutlinedButton
              size="small"
              onClick={() => handleReturn(asset)}
              disabled={
                isReturningAsset && returningAssetVariables.assetId === asset.id
              }
            >
              <Trans>Återlämna</Trans>
            </OutlinedButton>
          </div>
          <Text element="span" weight="medium">
            <Trans>Tilldelad</Trans>{" "}
            {humanFormat(asset.allocatedAt, { dateStyle: "short" })}
          </Text>
          <CommentEditor assetId={asset.id} comment={asset.comment ?? null} />
        </li>
      ))}
    </ul>
  );
};
